import { Rating, Stack, Button, Chip, Box, Alert, Typography } from '@mui/material';
import '../../css/product.css';
import { Edit } from '@mui/icons-material';
import Progress from '../Tools/Progress'
import { Link } from 'react-router-dom';
import AttachmentIcon from '@mui/icons-material/Attachment';
import ZoomIcon from '@mui/icons-material/ZoomInOutlined';
import ShareIcon from '@mui/icons-material/Share';
import EventIcon from '@mui/icons-material/Event';
import LockClockIcon from '@mui/icons-material/LockClock';
import moment from 'moment';
import { useContext } from "react"
import ToastContext from "../../services/Context/ToastContext"
import CopyToClipboard from 'react-copy-to-clipboard';
import UserContext from "../../services/Context/UserContext";
import { txt } from '../../services/translation';
import frenchCountries from "../../config/frenchCountries";
import VirtualClassroom from '../Tools/VirtualClassroom';

export default function Course({id, title, subtitle, program, picture, pi, button, rating, onClick, link, badges, completion, docs, dpc, session, virtualClassroomUrl, alert, shareLink, editable}){

    return <div className='product'>

        <Img id={id} url={picture} pi={pi} editable={editable} />

        <div className='product-content'>

            <Title title={title} subtitle={subtitle} />

            <Flag alert={alert} />

            <Stack direction='row' spacing={1}>

                <Program link={program} />

                <Share link={shareLink} />

            </Stack>

            <Stars rating={rating} />

            <Dpc dpc={dpc} />

            <Session session={session} />

            <VirtualClassroom url={virtualClassroomUrl} />

            <Completion value={completion} />

            <Docs link={docs} />

            <Badges values={badges || []} />

        </div>

        <Btn text={button} onClick={onClick} link={link} />

    </div>
}


function Title({title, subtitle}){

    return <span className='product-title'>
        {title}
        <br/>
        <small>{subtitle}</small>
    </span>

}



function Img({id, url, pi, editable}){

    const style = {backgroundImage: `url(${url})`}

    return <div className='product-img' style={style}>

        <div className='product-img-left'>

            {editable ? 
                <Link 
                    className='product-edit' 
                    to={`/design/${id}`}
                >
                    <Edit />
                </Link>
                :
                <div></div>
            }

            {pi && 
                <div className="product-pi">
                    <div className="product-pi-badge">PROGRAMME<br/>INTÉGRÉ DPC</div>
                </div>
            }

        </div>

    </div>
}


function Program({link}){

    return link &&
        <Button
            startIcon={<ZoomIcon />} 
            label="Programme" 
            href={link /*V1*/} 
            target="_blank" 
            rel="noopener noreferrer"
            size='small'
            color='info'
        >
            {txt('program')}
        </Button>
}

function Share({link}){

    const {setToast} = useContext(ToastContext)

    return link &&
        <CopyToClipboard 
            text={link} 
            onCopy={() => setToast({
                severity: 'success',  
                message: txt('copyEnrollmentLinkMessage'),
                duration: 4000
            })}
        >
            <Button
                startIcon={<ShareIcon />} 
                label={txt('shareButton')} 
                size='small'
                color='info'
            >
                {txt('shareButton')}
            </Button>
        </CopyToClipboard>
}


function Stars({rating}){

    if ((rating || 0) <= 3) return <></>;

    return <Stack direction='row' alignItems='center' spacing={1}>

        <Rating 
             value={rating}
            precision={0.5}
            readOnly
        />

        <span>{rating.toFixed(1)}</span>
     </Stack>;
}


function Docs({link}){

    return link && <Link to={link}>

        <Chip 
            icon={<AttachmentIcon />} 
            label={txt('documents')} 
            clickable
            variant='outlined'
        />

    </Link>
}



function Badges({values}){

    const {user} = useContext(UserContext)

    if(!frenchCountries.includes(user.country)) return <></>

    if(values.length === 0) return <></>

    return <Stack 
        direction='row' 
        justifyContent='center' 
        flexWrap='wrap'
    >

        {values.map(v => 
            <Chip 
                key={v} 
                label={v} 
                size='small'
                sx={{m: 0.5}}
            />)
        }

    </Stack>
}

function Dpc({dpc}){

    if (!dpc) return <></>;

    const texts = dpc.shortTitle?.split(' du ') ?? [];
    
    if (texts.length !== 2) return <></>;

    return <Chip
        label={<Typography variant="body2">{texts[0]}<br/>du {texts[1]}</Typography>}
        sx={{ width: '180px', height: '54px', borderRadius: '27px' }}
    />
}


function Session({session}){

    if (!session) return <></>;

    const format = d => moment(d).format('DD/MM/YYYY');

    const period = session.end
        ? `Du ${format(session.start)} au ${format(session.end)}`
        : `À partir du ${format(session.start)}`;

    const next = Object.values(session.program || {})
        .filter(d => d && moment(d).isAfter(moment()))
        .sort()[0];

    return <Stack spacing={0.5} sx={{width: '100%', textAlign: 'left'}}>

        <Stack direction='row' spacing={1} alignItems='center'>
            <EventIcon fontSize='small' color='action' />
            <Typography variant='body2' color='text.secondary'>{session.title}</Typography>
        </Stack>

        <Typography variant='body2' color='text.secondary'>{period}</Typography>

        {next &&
            <Stack direction='row' spacing={1} alignItems='center'>
                <LockClockIcon fontSize='small' color='action' />
                <Typography variant='body2' color='text.secondary'>
                    Prochain chapitre le {format(next)}
                </Typography>
            </Stack>
        }

    </Stack>
}


function Completion({value}){

    return (typeof value === 'number') &&
        <Box width="100%">
            <Progress value={value}/>
        </Box>

}


function Btn({text, onClick, link}){
    const isExternalLink = link && typeof link === 'string' && link.startsWith('https://');

    const isInternalLink = link && typeof link === 'string' && !link.startsWith('https://');

    const props = {
        sx: { width: '100%', height: 50 },
        color: 'info',
        onClick: onClick || undefined,
        component: isInternalLink ? Link : undefined,
        to: isInternalLink ? link : undefined,
        href: isExternalLink ? link : undefined,
        target: isExternalLink ? '_blank' : undefined,
        rel: isExternalLink ? 'noopener noreferrer' : undefined,
    }

    return <Button {...props}>{text}</Button>
}


function Flag({alert}){

    if(!Array.isArray(alert) || alert.length < 2) return false;

    return <Alert severity={alert[0]} size='small' sx={{p: '0px 10px'}}>{alert[1]}</Alert>
}